import { useState, useCallback } from 'react'
import { useVaults } from './useVaults.ts'
import { useEncryption } from './useEncryption.ts'
import { useIPFS } from './useIPFS.ts'
import {
  useVaultMetadata,
  type SaveVaultMetadataParams,
} from './useVaultMetadata.ts'
import { useActivityLog } from './useActivityLog.ts'
import { useNotifications } from './useNotifications.ts'
import { sendVaultCreatedEmailsWebplatform } from '@/services/notification/webplatformEmailSenders.ts'
import { useWallet } from './useWallet.ts'
import { useProfile } from './useProfile.ts'
import { env } from '@/config/env.ts'
import { getChainSlug } from '@/config/chains.ts'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'

/** Chain slug stored in IPFS payload for reference at decrypt time. */
const LIT_CHAIN = getChainSlug(env.chainId)

/** Contract expects bytes32 (32 bytes = 64 hex chars). SHA-256 is exactly 32 bytes. */
function toBytes32Hash(hexHash: string): `0x${string}` {
  const raw = hexHash.startsWith('0x') ? hexHash.slice(2) : hexHash
  const truncated = raw.slice(0, 64)
  if (truncated.length !== 64) {
    throw new Error(
      `Encryption hash must be at least 32 bytes (64 hex chars), got ${raw.length / 2} bytes`,
    )
  }
  return `0x${truncated}` as `0x${string}`
}

export interface CreateVaultInput {
  type: 'text' | 'file'
  content: string
  file?: File
  beneficiaryAddress: `0x${string}`
  beneficiaryEmail: string
  beneficiaryPhone?: string
  countdownMinutes: number
  vaultName?: string
}

/** The raw encrypted payload that can be offered as a downloadable backup. */
export interface EncryptedVaultPayload {
  version: 2
  vaultType: 'text' | 'file'
  originalFilename?: string
  ciphertext: string
  dataToEncryptHash: string
  chain: string
  exportedAt: string
}

type VaultStep =
  | 'idle'
  | 'encrypting'
  | 'uploading'
  | 'contract'
  | 'metadata'
  | 'notifying'
  | 'done'
  | 'error'

interface UseCreateVaultReturn {
  step: VaultStep
  loading: boolean
  error: string | null
  /** Raw encrypted payload, available after encryption succeeds (step >= 'uploading'). */
  encryptedPayload: EncryptedVaultPayload | null
  create: (input: CreateVaultInput) => Promise<boolean>
}

export function useCreateVault(): UseCreateVaultReturn {
  const { address } = useWallet()
  const vaults = useVaults()
  const enc = useEncryption()
  const ipfsHook = useIPFS()
  const metadata = useVaultMetadata()
  const activity = useActivityLog()
  const notif = useNotifications()
  const { getProfileByWallet } = useProfile()

  const [step, setStep] = useState<VaultStep>('idle')
  const [error, setError] = useState<string | null>(null)
  const [encryptedPayload, setEncryptedPayload] =
    useState<EncryptedVaultPayload | null>(null)

  const loading = step !== 'idle' && step !== 'done' && step !== 'error'

  const create = useCallback(
    async (input: CreateVaultInput): Promise<boolean> => {
      if (!address) {
        setError('Wallet not connected')
        return false
      }

      try {
        // 1. Connect encryption service if needed
        if (!enc.isConnected) {
          await enc.connect()
        }

        // 2. Encrypt vault content
        setStep('encrypting')
        setError(null)

        const totalVaults = await vaults.getTotalVaultsCount()
        const nextVaultId = Number(totalVaults)

        let encResult
        if (input.type === 'file' && input.file) {
          encResult = await enc.encryptFile(input.file, address)
        } else {
          encResult = await enc.encryptString(input.content, address)
        }

        if (!encResult) {
          const reason =
            enc.getLastError() ||
            enc.error ||
            'Encryption failed. Check the browser console for details.'
          console.error('[useCreateVault] Encryption failed.', {
            error: enc.getLastError() || enc.error,
            step: 'encrypt',
          })
          throw new Error(reason)
        }

        // Store the encrypted payload so the UI can offer a backup download.
        setEncryptedPayload({
          version: 2,
          vaultType: input.type,
          originalFilename: input.file?.name,
          ciphertext: encResult.ciphertext,
          dataToEncryptHash: encResult.dataToEncryptHash,
          chain: LIT_CHAIN,
          exportedAt: new Date().toISOString(),
        })

        await activity.logActivity('vault_encrypted', undefined, {
          type: input.type,
          beneficiary: input.beneficiaryAddress,
        })

        // 3. Upload to Pinata/IPFS: ciphertext + hash + chain (v2 payload)
        setStep('uploading')

        const uploadResult = await ipfsHook.uploadJSON(
          {
            version: 2,
            vaultType: input.type,
            originalFilename: input.file?.name,
            ciphertext: encResult.ciphertext,
            dataToEncryptHash: encResult.dataToEncryptHash,
            chain: LIT_CHAIN,
          },
          { name: `vault-${Date.now()}.json` },
        )

        // BL-M-03: do not log the CID. It's already on-chain via createVault();
        // duplicating it in the application database widens the data surface
        // accessible to admin roles for no functional benefit.
        await activity.logActivity('vault_uploaded', undefined, {
          status: 'uploaded',
          timestamp: Date.now(),
        })

        // 4. Create vault on contract
        setStep('contract')

        const contractResult = await vaults.createVault({
          cid: uploadResult.cid,
          encryptionHash: toBytes32Hash(encResult.dataToEncryptHash),
          beneficiary: input.beneficiaryAddress,
          countdownMinutes: BigInt(input.countdownMinutes),
        })

        if (!contractResult) {
          throw new Error('Contract transaction failed')
        }

        const vaultIdNum =
          contractResult.vaultId !== undefined
            ? Number(contractResult.vaultId)
            : nextVaultId
        if (
          contractResult.vaultId !== undefined &&
          contractResult.vaultId !== BigInt(nextVaultId)
        ) {
          console.warn(
            '[useCreateVault] On-chain vault id from receipt differs from pre-tx total count;',
            'using receipt id for Supabase metadata.',
            { fromReceipt: contractResult.vaultId.toString(), nextVaultId },
          )
        }
        const vaultId = vaultIdNum

        await activity.logActivity('vault_created', vaultId, {
          txHash: contractResult.hash,
          beneficiary: input.beneficiaryAddress,
        })

        // 5. Save metadata
        setStep('metadata')

        const metadataParams: SaveVaultMetadataParams = {
          vaultId,
          benefactorAddress: address,
          beneficiaryAddress: input.beneficiaryAddress,
          beneficiaryEmail: input.beneficiaryEmail,
          beneficiaryPhone: input.beneficiaryPhone,
          vaultType: input.type,
          vaultName: input.vaultName,
          originalFilename: input.file?.name,
        }
        const metadataRow = await metadata.save(metadataParams)
        if (!metadataRow) {
          throw new Error(
            'Vault exists on-chain but metadata could not be saved (e.g. Supabase auth or RLS). Sign in with a linked wallet and try again, or contact support.',
          )
        }

        // 6. Notify benefactor + beneficiary (webplatform templates A — email)
        setStep('notifying')
        const beneficiaryProfile = await getProfileByWallet(
          input.beneficiaryAddress,
        )
        const benefactorProfile = await getProfileByWallet(address)

        await sendVaultCreatedEmailsWebplatform({
          vaultId,
          vaultName: input.vaultName,
          countdownMinutes: input.countdownMinutes,
          benefactorAddress: address,
          beneficiaryAddress: input.beneficiaryAddress,
          beneficiaryEmail: input.beneficiaryEmail,
          benefactorEmail: benefactorProfile?.email ?? null,
          benefactorName: benefactorProfile?.name ?? null,
          notifyBeneficiaryEmail: beneficiaryProfile?.notify_email,
          notifyBenefactorEmail: benefactorProfile?.notify_email,
        })

        if (
          input.beneficiaryPhone &&
          beneficiaryProfile?.notify_sms !== false
        ) {
          await notif.sendSMS({
            to: input.beneficiaryPhone,
            message: `BitsLegacy: A digital legacy vault has been created for you. Visit BitsLegacy to access it.`,
          })
        }

        await activity.logActivity('notification_sent', vaultId, {
          email: input.beneficiaryEmail,
          phone: input.beneficiaryPhone,
        })

        setStep('done')
        return true
      } catch (e) {
        const rawMessage = e instanceof Error ? e.message : ''
        if (rawMessage.includes('Response is too long')) {
          setError(
            'File encryption failed because the encrypted payload exceeds the current Lit Action response limit. Please use a smaller file for now.',
          )
        } else {
          setError(getFriendlyTransactionError(e))
        }
        setStep('error')
        return false
      }
    },
    [
      address,
      enc,
      ipfsHook,
      vaults,
      metadata,
      activity,
      notif,
      getProfileByWallet,
    ],
  )

  return { step, loading, error, encryptedPayload, create }
}
